import { io, Socket } from 'socket.io-client'

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://localhost:3335'
)

/**
 * Evento de janela que manda as telas buscarem de novo o que pode ter mudado
 * enquanto o socket esteve fora (app em segundo plano no celular, rede caiu).
 * Os eventos perdidos nesse intervalo não voltam: só uma nova busca recupera.
 */
export const RESYNC_EVENT = 'app:resync'

class SocketService {
  private socket: Socket | null = null
  private listeners: Map<string, Set<(data: any) => void>> = new Map()
  private hasJoinedAgents = false
  // Salas de chat abertas: sem isso o reconnect voltava só para "agents" e a
  // conversa aberta parava de receber message:new até ser reaberta.
  private joinedChats = new Set<number>()
  private jaConectou = false

  connect(token: string): Socket {
    if (this.socket?.connected) {
      return this.socket
    }

    this.socket = io(SOCKET_URL, {
      path: '/socket',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    })

    this.socket.on('connect', () => {
      console.log('[SOCKET] Connected:', this.socket?.id)
      // Re-join agents room on reconnect
      if (this.hasJoinedAgents) {
        this.socket?.emit('join:agents')
        console.log('[SOCKET] Re-joined agents room after reconnect')
      }
      this.joinedChats.forEach((chatId) => this.socket?.emit('join:chat', chatId))
      // Re-register all listeners after reconnect
      this.reattachListeners()
      // Reconexão (não a primeira conexão): o que chegou com o socket fora não
      // vem mais por evento — as telas buscam de novo.
      if (this.jaConectou && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(RESYNC_EVENT, { detail: { origem: 'socket' } }))
      }
      this.jaConectou = true
    })

    this.socket.on('disconnect', (reason) => {
      console.log('[SOCKET] Disconnected:', reason)
    })

    this.socket.on('connect_error', (error) => {
      console.error('[SOCKET] Connection error:', error.message)
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`[SOCKET] Reconnected after ${attemptNumber} attempts`)
    })

    return this.socket
  }

  // Re-attach all stored listeners after reconnection
  private reattachListeners(): void {
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.off(event, callback)
        this.socket?.on(event, callback)
      })
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.listeners.clear()
      this.hasJoinedAgents = false
      this.joinedChats.clear()
      this.jaConectou = false
    }
  }

  /**
   * Celular que volta do segundo plano costuma estar com o socket morto (o SO
   * congela a aba) e o socket.io pode demorar a perceber. Força a reconexão
   * na hora em vez de esperar o backoff.
   */
  ensureConnected(): void {
    if (this.socket && !this.socket.connected) {
      this.socket.connect()
    }
  }

  getSocket(): Socket | null {
    return this.socket
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }

  // Join a chat room
  joinChat(chatId: number): void {
    this.joinedChats.add(chatId)
    this.socket?.emit('join:chat', chatId)
  }

  // Leave a chat room
  leaveChat(chatId: number): void {
    this.joinedChats.delete(chatId)
    this.socket?.emit('leave:chat', chatId)
  }

  // Join agents room for pending chats notifications
  joinAgents(): void {
    this.hasJoinedAgents = true
    this.socket?.emit('join:agents')
    console.log('[SOCKET] Joining agents room')
  }

  // Typing indicators
  startTyping(chatId: number): void {
    this.socket?.emit('typing:start', { chatId })
  }

  stopTyping(chatId: number): void {
    this.socket?.emit('typing:stop', { chatId })
  }

  // Mark messages as read
  markMessagesRead(chatId: number): void {
    this.socket?.emit('messages:read', { chatId })
  }

  // Subscribe to events
  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
    // Register on socket if connected
    if (this.socket) {
      this.socket.off(event, callback) // Remove first to avoid duplicates
      this.socket.on(event, callback)
    }
  }

  // Unsubscribe from events
  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback)
      this.socket?.off(event, callback)
    } else {
      this.listeners.delete(event)
      this.socket?.off(event)
    }
  }

  // Emit an event
  emit(event: string, data?: any): void {
    this.socket?.emit(event, data)
  }
}

export const socketService = new SocketService()
export default socketService

// Socket events constants
export const SOCKET_EVENTS = {
  CHAT: {
    NEW: 'chat:new',
    UPDATED: 'chat:updated',
    STARTED: 'chat:started',
    FINISHED: 'chat:finished',
    TRANSFERRED: 'chat:transferred',
    STATUS_CHANGED: 'chat:status-changed',
    UNREAD_UPDATE: 'chat:unread_update',
    ACTIVITY: 'chat:activity',
  },
  MESSAGE: {
    NEW: 'message:new',
    READ: 'messages:marked-read',
    EDITED: 'message:edited',
    DELETED: 'message:deleted',
    STATUS: 'message:status',
  },
  TYPING: {
    STARTED: 'typing:started',
    STOPPED: 'typing:stopped',
  },
  NOTIFICATION: {
    NEW: 'notification:new',
  },
  INTERNAL_TICKET: {
    NEW: 'internal-ticket:new',
    REPLY: 'internal-ticket:reply',
    RESOLVED: 'internal-ticket:resolved',
    TRANSFERRED: 'internal-ticket:transferred',
  },
}
